<script setup lang="ts">
import type { FormInst, FormRules } from 'naive-ui';
import { computed, reactive, ref, watch } from 'vue';
import {
  type EditFieldConfig,
  getEntityEditFields,
  getEntityEditTitle,
} from '@/types/contractManagement/editConfig';
import { safeStringify } from '@/utils/jsonHelpers';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';

// Re-export for backward compatibility
export type { EditFieldConfig };

interface Props {
  show: boolean;
  entityType: string;
  initialData?: Record<string, unknown>;
  rules?: FormRules;
  loading?: boolean;
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'submit', data: Record<string, unknown>): void;
}

const props = withDefaults(defineProps<Props>(), {
  initialData: () => ({}),
  rules: () => ({}),
  loading: false,
});

const emit = defineEmits<Emits>();

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const formRef = ref<FormInst | null>(null);
const formData = reactive<Record<string, unknown>>({});
const dateValues = reactive<Record<string, number | null>>({});

// Get field configurations from centralized config
const fields = computed<EditFieldConfig[]>(() => {
  return getEntityEditFields(props.entityType);
});

// Get dialog title from centralized config
const dialogTitle = computed(() => {
  return getEntityEditTitle(props.entityType);
});

// Initialize form data from initialData prop
function initializeFormData(): void {
  // Clear existing data
  for (const key of Object.keys(formData)) delete formData[key];
  for (const key of Object.keys(dateValues)) delete dateValues[key];

  for (const field of fields.value) {
    const initialValue = props.initialData[field.key];

    switch (field.type) {
      case 'date': {
        // Handle date fields
        const timestamp = getDateValue(initialValue);
        dateValues[field.key] = timestamp;
        if (timestamp) {
          const date = new Date(timestamp);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          formData[field.key] = `${year}-${month}-${day}`;
        } else {
          formData[field.key] = null;
        }

        break;
      }
      case 'select': {
        // For select fields, preserve the value as-is (enum/string)
        formData[field.key] = initialValue ?? null;

        break;
      }
      case 'tags': {
        // Tags stay an array end to end — safeStringify would turn them into a
        // string and the update would post a tag list of one long value.
        formData[field.key] = Array.isArray(initialValue) ? [...initialValue] : [];

        break;
      }
      default: {
        // Handle other field types
        if (initialValue === null || initialValue === undefined || initialValue === '') {
          formData[field.key] = field.required ? '' : null;
        } else {
          formData[field.key] = safeStringify(initialValue);
        }
      }
    }
  }
}

// Watch for changes in initialData
watch(
  () => props.initialData,
  () => {
    if (props.show) {
      initializeFormData();
    }
  },
  { deep: true }
);

// Watch for modal show/hide
watch(showModal, (newValue: boolean) => {
  if (newValue) {
    initializeFormData();
  }
});

// Get date value from various formats
function getDateValue(value: unknown): number | null {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    let d = new Date(value);
    if (!isNaN(d.getTime())) return d.getTime();

    const parts = value.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (parts) {
      d = new Date(parseInt(parts[3]), parseInt(parts[2]) - 1, parseInt(parts[1]));
      if (!isNaN(d.getTime())) return d.getTime();
    }
  }
  return null;
}

// Handle date update
function handleDateUpdate(fieldKey: string, val: number | null): void {
  if (val === null) {
    formData[fieldKey] = null;
    dateValues[fieldKey] = null;
  } else {
    const date = new Date(val);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    formData[fieldKey] = `${year}-${month}-${day}`;
    dateValues[fieldKey] = val;
  }
}

// Handle form submission
function handleSubmit(): void {
  void formRef.value?.validate(errors => {
    ((): void => {
      if (!errors) {
        const submitData: Record<string, unknown> = {};

        for (const field of fields.value) {
          let value = formData[field.key];

          // Apply custom transform if provided
          if (field.transform) {
            value = field.transform(value);
          } else {
            // Default transformations
            if (typeof value === 'string') {
              value = value.trim();
              if (value === '') {
                value = null;
              }
            }

            if (field.upper && typeof value === 'string' && value) {
              value = value.toUpperCase();
            }
          }

          submitData[field.key] = value;
        }

        emit('submit', submitData);
      }
    })();
  });
}

// Build form rules from field configs and provided rules
const formRules = computed<FormRules>(() => {
  const rules: FormRules = { ...props.rules };

  for (const field of fields.value) {
    if (!(field.key in rules)) {
      rules[field.key] = [];
    }

    // Add required rule if field is required
    if (field.required) {
      const fieldRules = rules[field.key];
      const rulesArray = Array.isArray(fieldRules) ? fieldRules : [fieldRules];
      const hasRequired = rulesArray.some(
        (rule: unknown) => typeof rule === 'object' && rule !== null && 'required' in rule
      );
      if (!hasRequired) {
        (rules[field.key] as unknown[]).push({
          required: true,
          message: `Please enter ${field.label.toLowerCase()}`,
          trigger: ['blur', 'input'],
        });
      }
    }

    // Add email validation if type is email
    if (field.type === 'email') {
      const fieldRules = rules[field.key];
      const rulesArray = Array.isArray(fieldRules) ? fieldRules : [fieldRules];
      const hasEmail = rulesArray.some(
        (rule: unknown) =>
          typeof rule === 'object' &&
          rule !== null &&
          'type' in rule &&
          (rule as { type: string }).type === 'email'
      );
      if (!hasEmail) {
        (rules[field.key] as unknown[]).push({
          type: 'email',
          message: 'Please enter a valid email address',
          trigger: ['blur', 'input'],
          validator: (_rule: unknown, value: string): boolean => {
            if (!value || value.trim() === '') return true;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(value);
          },
        });
      }
    }

    // Add custom validator if provided
    const customValidator = field.validator;
    if (customValidator) {
      (rules[field.key] as unknown[]).push({
        validator: (_rule: unknown, value: unknown): boolean | Error => {
          const result = customValidator(value);
          if (typeof result === 'string') {
            return new Error(result);
          }
          return result;
        },
        trigger: ['blur', 'input'],
      });
    }
  }

  return rules;
});
</script>

<template>
  <NModal
    v-model:show="showModal"
    preset="card"
    :title="dialogTitle"
    style="width: 600px"
    :mask-closable="false"
  >
    <NForm
      v-if="fields.length > 0"
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-placement="top"
    >
      <NFormItem
        v-for="field in fields"
        :key="field.key"
        :label="field.label"
        :path="field.key"
        :required="field.required"
      >
        <gaia-textarea
          v-if="field.type === 'textarea'"
          v-model:value="formData[field.key] as string | null"
          :placeholder="field.placeholder"
          :rows="field.rows || 3"
          style="width: 100%"
          :autosize="false"
        />
        <gaia-date-picker
          v-else-if="field.type === 'date'"
          v-model:value="dateValues[field.key]"
          type="date"
          :placeholder="field.placeholder || 'YYYY-MM-DD'"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          clearable
          inputable
          style="width: 100%"
          @update:value="(val: number | null) => handleDateUpdate(field.key, val)"
        />
        <gaia-select
          v-else-if="field.type === 'select'"
          v-model:value="formData[field.key] as string | null"
          :options="field.options || []"
          :placeholder="field.placeholder"
          clearable
          style="width: 100%"
        />
        <NDynamicTags
          v-else-if="field.type === 'tags'"
          v-model:value="formData[field.key] as string[]"
          :input-props="{ placeholder: field.placeholder }"
        />
        <gaia-switch
          v-else-if="field.type === 'switch'"
          v-model:value="formData[field.key] as string"
          :checked-value="field.enabledValue"
          :default-value="field.disabledValue"
          :unchecked-value="field.disabledValue"
          size="large"
        >
          <template #checked> {{ field.enabledValue }} </template>
          <template #unchecked> {{ field.disabledLabel }} </template>
        </gaia-switch>
        <gaia-input
          v-else
          v-model:value="formData[field.key] as string | null"
          :type="field.type === 'email' ? 'text' : 'text'"
          :placeholder="field.placeholder"
          :maxlength="field.maxLength"
          :style="field.upper ? 'width: 100%; text-transform: uppercase' : 'width: 100%'"
        />
      </NFormItem>
    </NForm>
    <div v-else style="padding: 24px; text-align: center; color: #999">
      No fields available for this entity type.
    </div>

    <template #footer>
      <NSpace justify="end">
        <NButton @click="showModal = false">Cancel</NButton>
        <NButton type="primary" :loading="loading" @click="handleSubmit">Update</NButton>
      </NSpace>
    </template>
  </NModal>
</template>
